import { useState, useMemo, useEffect } from 'react';
import { useAllCounties } from '../data/useAllCounties';

export default function GeographyPanel({ currentOverride, defaultState, onApply, onClose }) {
  const { counties: allCounties, loading: countiesLoading } = useAllCounties();

  const [mode, setMode] = useState(currentOverride?.mode ?? 'single');

  // ── Mode 1: Single County ─────────────────────────────────────────────────
  const [stateFilter1, setStateFilter1] = useState(currentOverride?.state ?? defaultState ?? '');
  const [search1, setSearch1] = useState('');
  const [selected1, setSelected1] = useState(null); // county object from allCounties

  // ── Mode 2: County Group ──────────────────────────────────────────────────
  const [stateFilter2, setStateFilter2] = useState(currentOverride?.state ?? defaultState ?? '');
  const [search2, setSearch2] = useState('');
  const [group, setGroup] = useState([]); // [{ fips, name }]

  // ── Mode 3: Census Tracts ─────────────────────────────────────────────────
  const [stateFilter3, setStateFilter3] = useState(currentOverride?.state ?? defaultState ?? '');
  const [countyFips3, setCountyFips3] = useState(currentOverride?.county ?? '');
  const [tractInput, setTractInput] = useState('');

  // Initialize selections from currentOverride once county list is loaded
  useEffect(() => {
    if (!allCounties.length || !currentOverride) return;
    if (currentOverride.mode === 'single') {
      const found = allCounties.find(
        c => c.state === currentOverride.state && c.county === currentOverride.county
      );
      if (found) setSelected1(found);
    } else if (currentOverride.mode === 'group') {
      setStateFilter2(currentOverride.state ?? '');
      setGroup(currentOverride.counties ?? []);
    } else if (currentOverride.mode === 'tracts') {
      setStateFilter3(currentOverride.state ?? '');
      setCountyFips3(currentOverride.county ?? '');
      setTractInput((currentOverride.tractGeoids ?? []).join('\n'));
    }
  }, [allCounties]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive unique states from county list for dropdowns
  const stateOptions = useMemo(() => {
    const map = new Map();
    allCounties.forEach(c => {
      if (!map.has(c.state)) map.set(c.state, c.stateName);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [allCounties]);

  // Mode 1: filtered county list
  const filtered1 = useMemo(() => {
    return allCounties.filter(c => {
      if (stateFilter1 && c.state !== stateFilter1) return false;
      if (search1 && !c.fullName.toLowerCase().includes(search1.toLowerCase())) return false;
      return true;
    }).slice(0, 200);
  }, [allCounties, stateFilter1, search1]);

  // Mode 2: available counties (excludes already-selected)
  const filtered2 = useMemo(() => {
    const selectedFips = new Set(group.map(g => g.fips));
    return allCounties.filter(c => {
      if (stateFilter2 && c.state !== stateFilter2) return false;
      if (selectedFips.has(c.county)) return false;
      if (search2 && !c.name.toLowerCase().includes(search2.toLowerCase())) return false;
      return true;
    }).slice(0, 200);
  }, [allCounties, stateFilter2, search2, group]);

  // Mode 3: counties for selected state
  const countiesForState3 = useMemo(() => {
    return allCounties.filter(c => c.state === stateFilter3);
  }, [allCounties, stateFilter3]);

  // Parse and validate tract GEOIDs from textarea
  const validTractGeoids = useMemo(() => {
    return tractInput
      .split(/[\n,\s]+/)
      .map(s => s.trim())
      .filter(s => /^\d{11}$/.test(s));
  }, [tractInput]);

  function handleApply() {
    if (mode === 'single') {
      if (!selected1) return;
      onApply({
        mode: 'single',
        state: selected1.state,
        county: selected1.county,
        label: selected1.fullName
      });
    } else if (mode === 'group') {
      if (!group.length || !stateFilter2) return;
      const names = group.map(c => c.name);
      const label = names.length <= 3
        ? names.join(' + ')
        : `${names.length} Counties`;
      onApply({ mode: 'group', state: stateFilter2, counties: group, label });
    } else if (mode === 'tracts') {
      if (!stateFilter3 || !countyFips3) return;
      const countyEntry = countiesForState3.find(c => c.county === countyFips3);
      const label = validTractGeoids.length > 0
        ? `Selected Tracts — ${countyEntry?.name ?? countyFips3}`
        : countyEntry?.fullName ?? countyFips3;
      onApply({
        mode: 'tracts',
        state: stateFilter3,
        county: countyFips3,
        label,
        tractGeoids: validTractGeoids
      });
    }
  }

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  const applyDisabled =
    (mode === 'single' && !selected1) ||
    (mode === 'group' && (!group.length || !stateFilter2)) ||
    (mode === 'tracts' && (!stateFilter3 || !countyFips3));

  return (
    <div className="gp-overlay" onClick={handleOverlayClick}>
      <div className="gp-panel">
        <div className="gp-header">
          <span className="gp-title">Choose Geography</span>
          <button className="gp-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="gp-mode-tabs">
          <button
            className={`gp-tab${mode === 'single' ? ' gp-tab--active' : ''}`}
            onClick={() => setMode('single')}
          >
            Single County
          </button>
          <button
            className={`gp-tab${mode === 'group' ? ' gp-tab--active' : ''}`}
            onClick={() => setMode('group')}
          >
            County Group
          </button>
          <button
            className={`gp-tab${mode === 'tracts' ? ' gp-tab--active' : ''}`}
            onClick={() => setMode('tracts')}
          >
            Census Tracts
          </button>
        </div>

        <div className="gp-body">
          {countiesLoading && (
            <div className="gp-loading">Loading counties…</div>
          )}

          {/* ── Mode 1: Single County ──────────────────────────────────── */}
          {!countiesLoading && mode === 'single' && (
            <div className="gp-mode-content">
              {selected1 && (
                <div className="gp-current">
                  Selected: <strong>{selected1.fullName}</strong>
                </div>
              )}
              <div className="gp-filters">
                <select
                  className="gp-select"
                  value={stateFilter1}
                  onChange={e => setStateFilter1(e.target.value)}
                >
                  <option value="">All states</option>
                  {stateOptions.map(([fips, name]) => (
                    <option key={fips} value={fips}>{name}</option>
                  ))}
                </select>
                <input
                  className="gp-search"
                  placeholder="Search counties…"
                  value={search1}
                  onChange={e => setSearch1(e.target.value)}
                />
              </div>
              <div className="gp-list">
                {filtered1.map(c => (
                  <button
                    key={c.fips5}
                    className={`gp-list-item${selected1?.fips5 === c.fips5 ? ' gp-list-item--active' : ''}`}
                    onClick={() => setSelected1(c)}
                  >
                    {c.fullName}
                  </button>
                ))}
                {filtered1.length === 0 && (
                  <div className="gp-empty">No counties match your search.</div>
                )}
              </div>
            </div>
          )}

          {/* ── Mode 2: County Group ───────────────────────────────────── */}
          {!countiesLoading && mode === 'group' && (
            <div className="gp-mode-content">
              <div className="gp-filters">
                <select
                  className="gp-select"
                  value={stateFilter2}
                  onChange={e => { setStateFilter2(e.target.value); setGroup([]); setSearch2(''); }}
                >
                  <option value="">Select a state…</option>
                  {stateOptions.map(([fips, name]) => (
                    <option key={fips} value={fips}>{name}</option>
                  ))}
                </select>
                <input
                  className="gp-search"
                  placeholder="Search counties…"
                  value={search2}
                  onChange={e => setSearch2(e.target.value)}
                  disabled={!stateFilter2}
                />
              </div>
              <div className="gp-dual-list">
                <div className="gp-dual-list-col">
                  <div className="gp-list-header">Available</div>
                  <div className="gp-list">
                    {!stateFilter2 && (
                      <div className="gp-empty">Select a state first.</div>
                    )}
                    {stateFilter2 && filtered2.map(c => (
                      <button
                        key={c.fips5}
                        className="gp-list-item"
                        onClick={() => setGroup(g => [...g, { fips: c.county, name: c.name }])}
                      >
                        {c.name}
                      </button>
                    ))}
                    {stateFilter2 && filtered2.length === 0 && (
                      <div className="gp-empty">All counties selected.</div>
                    )}
                  </div>
                </div>
                <div className="gp-dual-list-col">
                  <div className="gp-list-header">Selected ({group.length})</div>
                  <div className="gp-list">
                    {group.map(c => (
                      <button
                        key={c.fips}
                        className="gp-list-item gp-list-item--removable"
                        onClick={() => setGroup(g => g.filter(x => x.fips !== c.fips))}
                        title="Remove"
                      >
                        {c.name} <span className="gp-remove">✕</span>
                      </button>
                    ))}
                    {group.length === 0 && (
                      <div className="gp-empty">Click counties to add them.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Mode 3: Census Tracts ─────────────────────────────────── */}
          {!countiesLoading && mode === 'tracts' && (
            <div className="gp-mode-content">
              <div className="gp-filters">
                <select
                  className="gp-select"
                  value={stateFilter3}
                  onChange={e => { setStateFilter3(e.target.value); setCountyFips3(''); }}
                >
                  <option value="">Select a state…</option>
                  {stateOptions.map(([fips, name]) => (
                    <option key={fips} value={fips}>{name}</option>
                  ))}
                </select>
                <select
                  className="gp-select"
                  value={countyFips3}
                  onChange={e => setCountyFips3(e.target.value)}
                  disabled={!stateFilter3}
                >
                  <option value="">Select a county…</option>
                  {countiesForState3.map(c => (
                    <option key={c.county} value={c.county}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="gp-tract-section">
                <label className="gp-label">
                  Census tract GEOIDs (11-digit, one per line or comma-separated)
                  <br />
                  <em>Leave blank to use all tracts in the county</em>
                </label>
                <textarea
                  className="gp-textarea"
                  rows={7}
                  value={tractInput}
                  onChange={e => setTractInput(e.target.value)}
                  placeholder={'08031000100\n08031000200\n08031000300'}
                />
                {tractInput.trim() && (
                  <div className="gp-tract-count">
                    {validTractGeoids.length} valid GEOID{validTractGeoids.length !== 1 ? 's' : ''}
                    {tractInput.split(/[\n,\s]+/).filter(s => s.trim()).length > validTractGeoids.length && (
                      <span className="gp-tract-invalid">
                        {' '}({tractInput.split(/[\n,\s]+/).filter(s => s.trim()).length - validTractGeoids.length} invalid)
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="gp-footer">
          <button className="gp-btn gp-btn--cancel" onClick={onClose}>Cancel</button>
          <button
            className="gp-btn gp-btn--apply"
            onClick={handleApply}
            disabled={applyDisabled}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
